import React from "react";
import { XCircle, AlertTriangle } from "lucide-react";

interface ModalProps {
  id: string;
}

interface WarningProps extends ModalProps {
  onCancel: () => void;
  onConfirm: () => void;
}

interface ErrorProps extends ModalProps {
  onRetry: () => void;
  onClose?: () => void;
}

const Warning: React.FC<WarningProps> = ({ id, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between bg-black px-6 py-4 text-white">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10">
              <AlertTriangle className="h-5 w-5 text-warning-300" />
            </span>
            <div>
              <p className="text-sm uppercase tracking-widest text-gray-200">Confirm Resolution</p>
              <h2 className="text-xl font-semibold">Resolve Receiving Issue</h2>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="text-2xl leading-none text-white/70 transition hover:text-white focus:outline-none"
          >
            &times;
          </button>
        </div>

        <div className="space-y-4 px-6 py-6">
          <div className="rounded-2xl border border-warning-200 bg-warning-50/70 p-4">
            <p className="text-sm font-semibold text-warning-900">This action is irrevocable.</p>
            <p className="text-sm text-warning-800">You are about to resolve issue ID: {id}</p>
          </div>
          <p className="text-sm text-secondary-600">
            Please ensure all discrepancies have been reviewed before marking this record as resolved.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 border-t border-secondary-100 bg-secondary-50 px-6 py-4">
          <button
            className="inline-flex flex-1 items-center justify-center rounded-lg border border-secondary-200 px-4 py-3 text-sm font-semibold text-secondary-700 transition hover:bg-white"
            onClick={onCancel}
            type="button"
          >
            Keep Issue Open
          </button>
          <button
            className="inline-flex flex-1 items-center justify-center rounded-lg bg-black px-4 py-3 text-sm font-semibold text-white transition hover:bg-gray-900"
            onClick={onConfirm}
            type="button"
          >
            Confirm Resolution
          </button>
        </div>
      </div>
    </div>
  );
};

const Error: React.FC<ErrorProps> = ({ onRetry, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between bg-error-600 px-6 py-4 text-white">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10">
              <XCircle className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm uppercase tracking-widest text-white/80">Something went wrong</p>
              <h2 className="text-xl font-semibold">Resolution Failed</h2>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-2xl leading-none text-white/80 transition hover:text-white focus:outline-none"
            >
              &times;
            </button>
          )}
        </div>

        <div className="space-y-4 px-6 py-6">
          <p className="text-sm text-secondary-700">
            We were unable to resolve this issue. Please check your connection or try again in a few moments.
          </p>
        </div>

        <div className="border-t border-secondary-100 bg-secondary-50 px-6 py-4">
          <button
            className="inline-flex w-full items-center justify-center rounded-lg bg-black px-4 py-3 text-sm font-semibold text-white transition hover:bg-gray-900"
            onClick={onRetry}
            type="button"
          >
            Retry Resolution
          </button>
        </div>
      </div>
    </div>
  );
};

const ResolveIssueModals = {
  Warning,
  Error
};

export { ResolveIssueModals };
